import {AsyncPipe, CommonModule} from '@angular/common';
import {Component, HostListener, inject, signal} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatFormField, MatInputModule} from '@angular/material/input';
import {MatMenuModule} from '@angular/material/menu';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {RouterLink} from '@angular/router';
import {CovIconComponent, CovSkeletonComponent, IconName} from '@cov/ui-angular';
import {TranslocoPipe} from '@jsverse/transloco';
import {BehaviorSubject, catchError, filter, of, Subscription, switchMap, tap} from 'rxjs';
import {SkeletonAppearanceType} from '../../../../../../../../packages/ui-angular/src/lib/enums/skeleton-appearance.type';
import {ConjunctionType, ListRequest} from '../../../../interfaces/list-request.interface';
import {ORDERS, PAGE_SIZE_INFINITY, SEARCH_RESULT_MAX} from '../../../../shared/constants';
import {
  DATA_DIVIDER,
  DATA_DIVIDER_SECONDARY,
  SEARCH_MIN_CHARACTER,
} from '../../../../shared/constants/ui';
import {SearchHighlightDirective} from '../../../../shared/directives/search-highlight/search-highlight.directive';
import {clearHighlightContext} from '../../../../shared/directives/search-highlight/search-highlight.utils';
import {RemoveLeadingZerosPipe} from '../../../../shared/pipes/removeLeadingZeros.pipe';
import {DividerComponent} from '../../../../shared/ui/divider/divider.component';
import {getOrderItemDetailsLink} from '../../../../shared/utils/order-item.utils';
import {SearchModalState} from '../../interface/search.interface';
import {SearchFilterService} from '../../service/search-filter.service';
import {SearchService} from '../../service/search.service';
import {LocaleDatePipe} from '../../../../shared/pipes/locale-date.pipe';

@Component({
  selector: 'cov-search-bar',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormField,
    CovIconComponent,
    MatInputModule,
    MatMenuModule,
    AsyncPipe,
    TranslocoPipe,
    RouterLink,
    SearchHighlightDirective,
    CovSkeletonComponent,
    MatProgressSpinner,
    RemoveLeadingZerosPipe,
    DividerComponent,
    MatCheckbox,
    LocaleDatePipe,
  ],
  templateUrl: './search-bar.component.html',
  styleUrl: './search-bar.component.scss',
})
export class SearchBarComponent {
  //INJECTS
  protected filterService = inject(SearchFilterService);
  public searchService = inject(SearchService);

  //CONST
  protected readonly getOrderItemDetailsLink = getOrderItemDetailsLink;
  protected readonly IconName = IconName;
  public readonly SearchModalState = SearchModalState;
  protected readonly searchMaxLength = SEARCH_RESULT_MAX;
  protected readonly ordersRoute = ORDERS;
  protected readonly SkeletonAppearanceType = SkeletonAppearanceType;
  protected readonly DATA_DIVIDER = DATA_DIVIDER;
  protected readonly DATA_DIVIDER_SECONDARY = DATA_DIVIDER_SECONDARY;
  public readonly HIGHLIGHT_CONTEXT = 'searchComponent';

  // STATE
  protected subscription?: Subscription;
  public searchTerm$ = new BehaviorSubject('');
  public loading = signal(false);
  public advancedMode = signal(false);
  public searchResults$ = this.searchTerm$.pipe(
    tap(() => this.loading.set(false)),
    filter((searchTerm) => !!searchTerm && searchTerm.trim().length >= SEARCH_MIN_CHARACTER),
    tap((searchTerm) => {
      this.filterService.getSearchControl()?.setValue(searchTerm);
      this.loading.set(true);
    }),
    switchMap(() => this.searchService.search(this.getCurrentRequestData())),
    tap((searchResults) => {
      this.loading.set(false);
      if (
        !searchResults.groups?.length ||
        searchResults.groups.every((group) => !group.reports?.length)
      ) {
        this.setState(SearchModalState.NO_RESULTS);
      }
    }),
    catchError((e) => {
      this.loading.set(false);
      this.setState(SearchModalState.NO_RESULTS);
      return of({groups: []});
    }),
  );
  public allStates = SearchModalState;
  public modalState = signal<SearchModalState>(SearchModalState.CLOSED);

  public searchTermWithHighlightUpdate$ = this.searchTerm$.pipe(
    tap(() => clearHighlightContext(this.HIGHLIGHT_CONTEXT)),
  );

  // METHODS
  protected getCurrentRequestData(): ListRequest {
    return {
      currentPage: 0,
      pageSize: PAGE_SIZE_INFINITY,
      sort: '',
      sortDirection: '',
      filters: this.filterService.exportFilter(),
      conjunctionType: ConjunctionType.OR,
      mode: this.advancedMode() ? 'ADVANCED' : 'BASIC',
    };
  }

  protected updateSearchTerm(event: any) {
    if (this.searchTerm$.value !== event.target.value) {
      this.searchTerm$.next(event.target.value);
    }
  }

  public ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  protected setState(state: SearchModalState) {
    this.modalState.set(state);
  }

  protected updateState(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    if (inputElement.value.trim().length) {
      this.setState(SearchModalState.RESULTS);
    } else {
      this.setState(SearchModalState.INFO);
    }
  }

  @HostListener('document:click', ['$event'])
  handleOutsideClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    if (
      !target.closest('.cov-search-bar-menu') &&
      !target.classList.contains('cov-search-bar-input')
    ) {
      this.setState(SearchModalState.CLOSED);
    }
  }
}
